import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, Save, BookOpen, Lock, CheckCircle2, Send, ShieldCheck, FileImage } from "lucide-react";
import TeacherAnswerSheetDialog from "@/components/TeacherAnswerSheetDialog";
import PrePrimaryAssessments from "@/pages/PrePrimaryAssessments";

const CLASSES = ["Play Group","LKG","UKG","1","2","3","4","5","6","7","8","9","10"];
const PRE_PRIMARY = ["Play Group","LKG","UKG"];
const SECTIONS = ["A","B"];
const SUBJECTS = ["Kannada","English","Mathematics","Science","Social Studies","Hindi","Computer","Sanskrit"];

export default function Assessments({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isReadOnly = user?.role === "super_admin";

  // Default class/section: teacher's first assignment, or stored selection
  const firstAssignment = user?.assignments?.[0];
  const [classSel, setClassSel] = useState(localStorage.getItem("sa_selected_class") || firstAssignment?.class_name || "5");
  const [secSel, setSecSel] = useState(localStorage.getItem("sa_selected_section") || firstAssignment?.section || "A");
  const [subjectSel, setSubjectSel] = useState("");

  const [meta, setMeta] = useState(null);
  const [classMeta, setClassMeta] = useState({ classes: [], assignments: [] });
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sheetStudent, setSheetStudent] = useState(null);

  // Local edits keyed (student_id|exam_key) → { marks, max_marks }
  const [edits, setEdits] = useState({});

  useEffect(() => {
    api.get("/meta/classes").then(({ data }) => setClassMeta(data)).catch(() => {});
  }, []);

  // Class/section options scoped to what the teacher is assigned (admins get all).
  const classOptions = classMeta.classes?.length ? classMeta.classes : [classSel];
  const sectionOptions = (() => {
    const secs = (classMeta.assignments || [])
      .filter((a) => a.class_name === classSel)
      .map((a) => a.section);
    const uniq = [...new Set(secs)].sort();
    return uniq.length ? uniq : (secSel ? [secSel] : []);
  })();

  // Keep the selected section valid when the class changes.
  useEffect(() => {
    if (sectionOptions.length && !sectionOptions.includes(secSel)) {
      setSecSel(sectionOptions[0]);
    }
    // eslint-disable-next-line
  }, [classSel, classMeta]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assessments/meta");
        setMeta(data);
        if (data.teacher_subject) setSubjectSel(data.teacher_subject);
        else setSubjectSel((s) => s || SUBJECTS[0]);
      } catch (e) { toast.error("Could not load assessment metadata"); }
    })();
  }, []);

  const load = async () => {
    if (!classSel || !secSel) return;
    if (PRE_PRIMARY.includes(classSel)) return;  // pre-primary uses its own grid
    setLoading(true);
    try {
      const params = { class_name: classSel, section: secSel };
      if (subjectSel) params.subject = subjectSel;
      const { data } = await api.get("/assessments", { params });
      setGrid(data);
      setEdits({});
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load assessments");
      setGrid(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (meta) load(); /* eslint-disable-next-line */ }, [classSel, secSel, subjectSel, meta]);

  const columns = meta?.columns || [];
  const currentKey = grid?.current_key ?? meta?.current_key;

  const isPast = (col) => {
    if (!currentKey) return false;
    const cur = columns.findIndex((c) => c.key === currentKey);
    const idx = columns.findIndex((c) => c.key === col.key);
    return idx < cur;
  };
  const isFuture = (col) => {
    if (!currentKey) return true;
    const cur = columns.findIndex((c) => c.key === currentKey);
    const idx = columns.findIndex((c) => c.key === col.key);
    return idx > cur;
  };
  const isCurrent = (col) => col.key === currentKey;

  // Max marks per test come from the admin's per-class config; fall back to the
  // format default (Formative/MT* = 40, Term = 80) when not configured.
  const maxForKey = (key) => grid?.max_by_key?.[key] ?? (String(key).startsWith("MT") ? 40 : 80);

  // Grade-only subjects (Hindi) render a grade dropdown instead of a number input.
  const gradeOnly = !!grid?.grade_only;
  const gradeOptions = grid?.grade_options || ["A+", "A", "B+", "B", "C+", "C", "D"];
  const gradeDefault = grid?.grade_default || "A";

  const setCell = (sid, key) => (e) => {
    if (gradeOnly) {
      // The <select> emits a plain string. "AB" is the "Absent" sentinel we add
      // to the option list ourselves; empty string clears the cell.
      const raw = String(e.target.value || "");
      let patch;
      if (raw === "") patch = { grade: null, absent: false };
      else if (raw === "AB") patch = { grade: null, absent: true };
      else patch = { grade: raw, absent: false };
      setEdits((prev) => ({
        ...prev,
        [`${sid}|${key}`]: { ...(prev[`${sid}|${key}`] || {}), ...patch },
      }));
      return;
    }
    const t = String(e.target.value).trim().toUpperCase();
    let patch;
    if (t === "") patch = { marks: null, absent: false };
    else if (t[0] === "A") patch = { marks: null, absent: true };
    else {
      const raw = t.replace(/[^0-9]/g, "");
      let n = raw === "" ? null : Number(raw);
      if (n != null && n > maxForKey(key)) n = maxForKey(key);  // cap to format max
      patch = { marks: n, absent: false };
    }
    setEdits((prev) => ({
      ...prev,
      [`${sid}|${key}`]: { ...(prev[`${sid}|${key}`] || {}), ...patch },
    }));
  };

  // Display value for a cell: "AB" when absent, else marks/grade (edited or saved).
  const cellDisplay = (sid, key) => {
    const e = edits[`${sid}|${key}`];
    const src = e || grid?.students?.find((x) => x.student_id === sid)?.cells?.[key] || {};
    if (src.absent) return "AB";
    if (gradeOnly) return src.grade || gradeDefault;   // default A for un-set grade-only cells
    return src.marks == null ? "" : src.marks;
  };

  const dirtyCount = useMemo(() => Object.keys(edits).length, [edits]);

  // Live entry summary for the active test (saved cells + unsaved edits).
  const entrySummary = useMemo(() => {
    if (!grid || !currentKey) return null;
    let entered = 0, absent = 0;
    for (const s of grid.students) {
      const e = edits[`${s.student_id}|${currentKey}`];
      const src = e || s.cells?.[currentKey] || {};
      if (src.absent) absent++;
      else if (gradeOnly) {
        // Grade-only subjects auto-default to "A" so every student is
        // always "entered" for reporting purposes.
        entered++;
      } else if (src.marks != null) entered++;
    }
    const total = grid.students.length;
    return { total, entered, absent, pending: total - entered - absent };
  }, [grid, edits, currentKey, gradeOnly]);

  const save = async () => {
    if (isReadOnly) { toast.error("Super admin is read-only."); return; }
    if (!isAdmin && !currentKey) { toast.error("No active assessment window today."); return; }
    // Each edit key already encodes its own exam column, so admins can correct
    // marks for any test (past/published), while teachers' non-active columns
    // are ignored by the server.
    const cells = Object.entries(edits).map(([k, v]) => {
      const [student_id, exam_key] = k.split("|");
      return { student_id, exam_key, marks: v.marks ?? null, absent: !!v.absent, grade: v.grade ?? null };
    });
    if (!cells.length) { toast.message("Nothing to save."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/assessments", {
        class_name: classSel, section: secSel, subject: subjectSel, cells,
      });
      toast.success(`Saved ${data.saved} · cleared ${data.cleared}.`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const subjectSubmitted = !!grid?.submitted;
  const canSubmit = !!currentKey && !isReadOnly && !subjectSubmitted && entrySummary && entrySummary.total > 0 && entrySummary.pending === 0;

  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post("/assessments/submit", { class_name: classSel, section: secSel, subject: subjectSel, exam_key: currentKey });
      toast.success("Marks submitted. This subject is now locked.");
      setShowPreview(false);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  };

  const Shell = embedded ? Fragment : Layout;
  const currentLabel = columns.find((c) => c.key === currentKey)?.label;
  const closeDateShort = (() => {
    if (!meta?.close_date) return null;
    const d = new Date(meta.close_date + (meta.close_date.length === 10 ? "T00:00:00" : ""));
    return isNaN(d) ? meta.close_date : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  })();
  const daysLeft = (() => {
    if (!meta?.close_date) return null;
    const d = new Date(meta.close_date + (meta.close_date.length === 10 ? "T00:00:00" : ""));
    if (isNaN(d)) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((d - today) / 86400000);
  })();
  const daysLeftText = daysLeft == null ? null
    : daysLeft < 0 ? "overdue"
    : daysLeft === 0 ? "today"
    : daysLeft === 1 ? "1 day left"
    : `${daysLeft} days left`;
  const daysUrgent = daysLeft != null && daysLeft <= 2;
  const marksClosed = meta && !isAdmin && !currentKey;
  const marksOpen = meta && !isAdmin && !!currentKey;
  const closedPill = (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 h-9 shrink-0"
      title="No test is open for marks entry right now. Please check back later."
      data-testid="marks-entry-banner"
      data-open="false"
    >
      <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300 shrink-0" />
      <span className="text-xs font-semibold text-amber-900 dark:text-amber-200 whitespace-nowrap">Marks entry closed</span>
    </div>
  );
  const openPill = (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-emerald-300/70 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 h-9 shrink-0"
      title={meta?.close_date ? `Enter marks before ${meta.close_date}. Other tests are locked.` : "Enter marks for the open test. Other tests are locked."}
      data-testid="marks-entry-banner"
      data-open="true"
    >
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300 shrink-0" />
      <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-200 whitespace-nowrap">
        Marks entry open{currentLabel ? <> · <span className="font-bold">{currentLabel}</span></> : ""}
        {closeDateShort ? <span className="font-normal text-emerald-700/80 dark:text-emerald-300/70"> · auto-closes {closeDateShort}</span> : ""}
      </span>
      {daysLeftText && (
        <span
          className={`marks-due-blink inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
            daysUrgent ? "bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300" : "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300"
          }`}
          data-testid="marks-days-left"
        >
          {daysLeftText}
        </span>
      )}
    </div>
  );
  const statusPill = marksOpen ? openPill : marksClosed ? closedPill : null;

  // Pre-primary classes get a dedicated grade-based grid (marks out of 10 →
  // grade). Switching the class picker back to a regular class returns here.
  if (PRE_PRIMARY.includes(classSel)) {
    return (
      <PrePrimaryAssessments
        embedded={embedded}
        classSel={classSel}
        secSel={secSel}
        setClassSel={setClassSel}
        setSecSel={setSecSel}
        classOptions={classOptions}
        sectionOptions={sectionOptions}
        isAdmin={isAdmin}
        isReadOnly={isReadOnly}
        academicYear={meta?.academic_year}
      />
    );
  }

  return (
    <Shell>
      {!embedded && (
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2">Assessments</div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
              Marks entry
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {meta?.academic_year ? `Academic year ${meta.academic_year}` : ""}
            </p>
          </div>
          {statusPill}
        </div>
      )}

      {/* Admin can correct any test (incl. published). Teachers see the active-window banner. */}
      {meta && isAdmin && !isReadOnly && (
        <div className="mb-6 rounded-2xl border border-indigo-300/70 dark:border-indigo-800 bg-gradient-to-r from-indigo-500 to-violet-600 text-white px-4 sm:px-5 py-3 flex items-center gap-3" data-testid="marks-entry-admin-banner">
          <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>Admin edit mode — you can correct marks for any test</div>
            <div className="text-xs text-white/85 mt-0.5">Pick a class, section &amp; subject, edit any column (including published tests), then Save. After correcting a published result, use <b>Re-send</b> on the Results page to notify parents again.</div>
          </div>
        </div>
      )}

      {/* Marks-entry status pill (teachers). In the full page it lives on the
          header row; in embedded mode it's shown right-aligned here. */}
      {embedded && statusPill && (
        <div className="mb-6 flex justify-end">{statusPill}</div>
      )}

      {/* Filters */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <FilterBox label="Class">
          <Select value={classSel} onValueChange={setClassSel}>
            <SelectTrigger className="h-11" data-testid="assess-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>{classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </FilterBox>
        <FilterBox label="Section">
          <Select value={secSel} onValueChange={setSecSel}>
            <SelectTrigger className="h-11" data-testid="assess-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </FilterBox>
        <FilterBox label="Subject">
          {(() => {
            const teacherSubjects = meta?.teacher_subjects || [];
            const adminFree = isAdmin && !meta?.teacher_subject;
            const showSelect = adminFree || teacherSubjects.length > 1;
            const options = adminFree ? SUBJECTS : teacherSubjects;
            return showSelect ? (
              <Select value={subjectSel} onValueChange={setSubjectSel}>
                <SelectTrigger className="h-11" data-testid="assess-subject-select"><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <div className="h-11 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center gap-2 text-sm">
                <BookOpen className="h-4 w-4 text-slate-400" /> <span className="font-medium">{subjectSel || "—"}</span>
                <span className="ml-auto text-[10px] uppercase tracking-[0.15em] text-slate-400">your subject</span>
              </div>
            );
          })()}
        </FilterBox>
        <FilterBox label="Actions">
          <div className="flex gap-2">
            <Button
              onClick={save}
              disabled={saving || isReadOnly || dirtyCount === 0 || (!isAdmin && (!currentKey || subjectSubmitted))}
              className="h-11 flex-1 px-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60"
              title={dirtyCount === 0 ? "No unsaved changes" : "Save entered marks"}
              data-testid="assess-save-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? "Saving…" : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ""}`}
            </Button>
            <Button
              onClick={() => setShowPreview(true)}
              disabled={!canSubmit || dirtyCount > 0}
              title={subjectSubmitted ? "Already submitted" : dirtyCount > 0 ? "Save your changes first" : (canSubmit ? "Preview & submit" : "Enter marks for all students first")}
              className="h-11 flex-1 px-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              data-testid="assess-submit-btn"
            >
              {subjectSubmitted ? <ShieldCheck className="h-4 w-4 mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              {subjectSubmitted ? "Submitted" : "Submit"}
            </Button>
          </div>
        </FilterBox>
      </div>

      {/* Grid */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden" data-testid="assessments-grid">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : !grid ? (
          <div className="p-10 text-center text-slate-500">Select a class to begin.</div>
        ) : grid.students.length === 0 ? (
          <div className="p-12 text-center" data-testid="assess-empty">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400 flex items-center justify-center mb-3">
              <GraduationCap className="h-6 w-6" />
            </div>
            <div className="text-slate-700 dark:text-slate-200 font-semibold" style={{ fontFamily: "Outfit" }}>
              No students in Class {classSel}-{secSel}
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
              This section has no enrolled students yet. Pick another class/section above, or add students via <span className="font-medium">Settings → Imports</span>.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="px-4 pt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="assess-absent-hint">
                Tip: type <b className="text-rose-600">A</b> in a cell to mark the student <b>Absent</b> for the test.
              </div>
              {entrySummary && currentKey && (
                <div className="flex items-center gap-2 text-xs" data-testid="assess-entry-summary">
                  {subjectSubmitted && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 font-semibold" data-testid="assess-submitted-badge"><ShieldCheck className="h-3 w-3" /> Submitted &amp; locked</span>
                  )}
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 font-semibold">Entered {entrySummary.entered}</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 px-2.5 py-1 font-semibold">Absent {entrySummary.absent}</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 px-2.5 py-1 font-semibold">Pending {entrySummary.pending}</span>
                  <span className="text-slate-400">of {entrySummary.total}</span>
                </div>
              )}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 sticky left-0 bg-slate-50 dark:bg-slate-900/40 z-10 w-44">Roll · Student</th>
                  {columns.map((col) => {
                    const state = isCurrent(col) ? "current" : isPast(col) ? "past" : "future";
                    const headTone = {
                      current: "text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/30",
                      past: "text-slate-500 dark:text-slate-400",
                      future: "text-slate-300 dark:text-slate-600",
                    }[state];
                    return (
                      <th key={col.key} className={`text-center px-3 py-3 ${headTone}`} data-testid={`assess-col-${col.key}`}>
                        <div className="text-xs font-semibold uppercase tracking-wide flex items-center justify-center gap-1">
                          {state === "current" && <CheckCircle2 className="h-3 w-3" />}
                          {state === "future" && <Lock className="h-3 w-3" />}
                          {col.short || col.label}
                        </div>
                        <div className="text-[10px] font-normal normal-case text-slate-400 mt-0.5">out of {maxForKey(col.key)}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {grid.students.map((s, idx) => (
                  <tr key={s.student_id} className={`border-b border-slate-100 dark:border-slate-800 ${idx % 2 ? "bg-slate-50/40 dark:bg-slate-900/20" : ""}`}>
                    <td className="px-4 py-2 sticky left-0 bg-inherit z-10">
                      <button type="button" onClick={() => setSheetStudent(s)} className="flex items-center gap-2 text-left group" data-testid={`assess-student-${s.roll_number}`} title="Manage answer sheets">
                        <div className="h-8 w-8 rounded-lg bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center text-xs font-semibold shrink-0">
                          {s.roll_number?.slice(-2) || idx + 1}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 dark:text-slate-100 truncate group-hover:text-sky-600 flex items-center gap-1">{s.name}<FileImage className="h-3 w-3 text-slate-300 group-hover:text-sky-500" /></div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">{s.roll_number}</div>
                        </div>
                      </button>
                    </td>
                    {columns.map((col) => {
      const editable = (isReadOnly ? false : (isAdmin || (isCurrent(col) && !subjectSubmitted)));
                      const past = isPast(col);
                      const k = `${s.student_id}|${col.key}`;
                      const dirty = !!edits[k];
                      return (
                        <td key={col.key} className={`px-2 py-2 ${past ? "bg-slate-50/60 dark:bg-slate-900/30" : ""}`}>
                          <div className="flex items-center justify-center">
                            {gradeOnly ? (
                              <select
                                disabled={!editable}
                                value={cellDisplay(s.student_id, col.key)}
                                onChange={setCell(s.student_id, col.key)}
                                className={`h-9 w-20 text-center text-sm rounded-md border ${dirty ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-300 dark:border-slate-700"} ${!editable ? "bg-slate-50 dark:bg-slate-900/40 text-slate-500" : "bg-white dark:bg-slate-950"} ${cellDisplay(s.student_id, col.key) === "AB" ? "text-rose-600 font-semibold uppercase" : ""}`}
                                data-testid={`assess-grade-${s.student_id}-${col.key}`}
                              >
                                {gradeOptions.map((g) => (
                                  <option key={g} value={g}>{g}</option>
                                ))}
                                <option value="AB">AB</option>
                              </select>
                            ) : (
                              <Input
                                type="text"
                                disabled={!editable}
                                value={cellDisplay(s.student_id, col.key)}
                                onChange={setCell(s.student_id, col.key)}
                                placeholder="—"
                                title="Enter marks, or type A for Absent"
                                className={`h-9 w-20 text-center text-sm ${dirty ? "border-amber-400 ring-2 ring-amber-100" : ""} ${!editable ? "bg-slate-50 dark:bg-slate-900/40 text-slate-500" : ""} ${cellDisplay(s.student_id, col.key) === "AB" ? "text-rose-600 font-semibold uppercase" : ""}`}
                                data-testid={`assess-marks-${s.student_id}-${col.key}`}
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isReadOnly && (
        <div className="mt-4 text-xs text-purple-600 dark:text-purple-300 inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Super admin · read-only access.
        </div>
      )}

      <Dialog open={showPreview} onOpenChange={(v) => !v && setShowPreview(false)}>
        <DialogContent className="sm:max-w-lg" data-testid="submit-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Send className="h-5 w-5 text-emerald-600" /> Submit {subjectSel} marks
            </DialogTitle>
            <DialogDescription>
              Review the marks for <b>{classSel}-{secSel}</b> ({columns.find((c) => c.key === currentKey)?.label}). After you confirm, this subject locks until an admin reopens it.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
            {grid?.students?.map((s) => {
              const e = edits[`${s.student_id}|${currentKey}`];
              const src = e || s.cells?.[currentKey] || {};
              // Grade-only subjects (Hindi) show the letter; unentered rows
              // fall back to the school-wide default (A) so the preview
              // matches what will actually be submitted.
              let val;
              if (src.absent) val = "Absent";
              else if (gradeOnly) val = src.grade || gradeDefault;
              else val = src.marks == null ? "—" : src.marks;
              return (
                <div key={s.student_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`preview-row-${s.student_id}`}>
                  <span className="text-slate-700 dark:text-slate-200 truncate">{s.roll_number} · {s.name}</span>
                  <span className={`font-semibold ${src.absent ? "text-rose-600" : "text-slate-900 dark:text-slate-100"}`}>{val}</span>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={confirmSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-submit-btn">
              {submitting ? "Submitting…" : "Confirm submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TeacherAnswerSheetDialog
        open={!!sheetStudent}
        onClose={() => setSheetStudent(null)}
        student={sheetStudent}
        examOptions={columns}
        defaultExamKey={currentKey}
      />
    </Shell>
  );
}

function FilterBox({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
